import * as rd from '@devexperts/remote-data-ts'
import { isPending } from '@reduxjs/toolkit'
import { cn, Dialog } from '@spiaggeit/spit-ui'
import { t } from 'i18next'
import { memo, useState } from 'react'
import { NavLink } from 'react-router-dom'

import { ArrowLeft } from '@/assets/icons/ArrowLeft'
import { Info } from '@/assets/icons/Info'
import { WhiteLabelBar } from '@/components/WhiteLabelBar'
import {
  CHOOSE_PRODUCT_PATH,
  INSERT_DATA_PATH,
  INSERT_PERIOD_PATH,
  LANDING_KIOSK_PATH,
  PAY_PATH,
  PAYMENT_KIOSK_PATH,
  THANK_YOU_PATH,
} from '@/app/router/paths'
import { useAppSelector } from '@/hooks/store'
import { useBackLink } from '@/hooks/useBackLink'
import { useCurrentPath } from '@/hooks/useCurrentPath'
import { useCurrentPathNotes } from '@/hooks/useCurrentPathNotes'
import { useIsInIFrame } from '@/hooks/useIsInIFrame'
import {
  isKioskSelector,
  isWebsiteSelector,
  isWidgetSelector,
} from '@/store/appSlice'
import { selectIsProgressBarPending } from '@/store/extraSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { beachLocationSelector } from '@/store/licenseSlice/selectors/beachLocationSelector'
import { servicesSlice } from '@/store/servicesSlice'
import { selectIsWhiteLabelActive, widgetSlice } from '@/store/widgetSlice'
import { DangerousHtml } from '../DangerousHtml/DangerousHtml'
import { LoaderTrigger } from '../LoaderTrigger'
import { InsertPeriodLink } from './InsertPeriodLink'
import { ProgressBar } from './ProgressBar'
import { TopBar } from './TopBar'

export const Header = memo(function Header() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { currentPath } = useCurrentPath()
  const backLink = useBackLink()
  const notes = useCurrentPathNotes()
  const beachLocation = useAppSelector(beachLocationSelector)
  const isWidget = useAppSelector(isWidgetSelector)
  const isWebsite = useAppSelector(isWebsiteSelector)
  const isKiosk = useAppSelector(isKioskSelector)
  const license = useAppSelector(licenseSlice.selectors.licenseRemoteData)
  const services = useAppSelector(servicesSlice.selectors.services)
  const isProgressBarPending = useAppSelector(selectIsProgressBarPending)
  const whiteLabel = useAppSelector(widgetSlice.selectors.whiteLabel)
  const isWhiteLabelActive = useAppSelector(selectIsWhiteLabelActive)
  const isInIFrame = useIsInIFrame()
  const isPayPath = currentPath === PAY_PATH

  const isLoadingProgressBar = isPayPath
    ? isPending(services)
    : isProgressBarPending

  const isLoadingHeader =
    !license ||
    isLoadingProgressBar ||
    (isWhiteLabelActive && !rd.isSuccess(whiteLabel))

  const isInsertPeriodPath = currentPath === INSERT_PERIOD_PATH
  const isInsertDataPath = currentPath === INSERT_DATA_PATH

  if (isLoadingHeader) {
    return <LoaderTrigger />
  }

  if (
    (isKiosk && INSERT_DATA_PATH === currentPath) ||
    [LANDING_KIOSK_PATH, PAYMENT_KIOSK_PATH].includes(currentPath)
  ) {
    return null
  }

  return (
    <>
      <header
        className={cn('sticky top-0 z-100 bg-white', {
          hidden: isInIFrame,
          'lg:shadow': isInsertPeriodPath && !isWidget,
          shadow: !isInsertPeriodPath,
        })}
      >
        <WhiteLabelBar />
        <TopBar />

        {currentPath !== THANK_YOU_PATH &&
        !(isInsertPeriodPath && isWhiteLabelActive) ? (
              <div className="container lg:grid lg:h-16 lg:grid-cols-2 lg:gap-8">
                <div
                  className={cn('flex min-w-0 items-center gap-3', {
                    'hidden lg:flex': isInsertDataPath || isPayPath,
                    'py-2 lg:py-3':
                  !isInsertPeriodPath ||
                  (!isWhiteLabelActive && isInsertPeriodPath),
                  })}
                >
                  {backLink && !isWebsite && (!isKiosk || isInsertPeriodPath) ? (
                    <NavLink to={backLink}>
                      <ArrowLeft className="size-8" />
                    </NavLink>
                  ) : null}

                  <div
                    className={cn('flex min-w-0 gap-2 lg:pr-3', {
                      hidden: isWhiteLabelActive,
                    })}
                  >
                    <div className="min-w-0">
                      {rd.isSuccess(license) ? (
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap font-bold leading-tight text-primary">
                          {license.value.license.name ?? ''}
                        </div>
                      ) : null}

                      <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm text-secondary">
                        {beachLocation ?? ''}
                      </div>
                    </div>

                    {notes ? (
                      <button
                        aria-label={t('common.moreInfo')}
                        className="shrink-0 self-start text-primary"
                        onClick={() => {
                          setIsDialogOpen(true)
                        }}
                        type="button"
                      >
                        <Info className="size-5" />
                      </button>
                    ) : null}
                  </div>

                  <InsertPeriodLink className="ml-auto lg:ml-0" />
                </div>

                <ProgressBar
                  className={cn({
                    hidden: [
                      INSERT_PERIOD_PATH,
                      THANK_YOU_PATH,
                      CHOOSE_PRODUCT_PATH,
                    ].includes(currentPath),
                  })}
                />
              </div>
            ) : null}
      </header>

      {notes ? (
        <Dialog
          isOpen={isDialogOpen}
          mobileMode="bottomSheet"
          scrollContent
          setIsOpen={setIsDialogOpen}
          size="sm"
          title={
            rd.isSuccess(license) ? license.value.license.name ?? '' : null
          }
          trigger={null}
        >
          <DangerousHtml className="p-4" html={notes} />
        </Dialog>
      ) : null}
    </>
  )
})
